import {
    createEmptyEssencePool,
    getStoredLoadout,
    isEmpyrealCrystalItem,
    withStartingCrystalPicksMoved,
    isBothHanded,
    withoutLastStartingCrystalPick,
    resolveStartingCrystalPicks,
    withEssenceSubtracted,
    type AccountCharacter,
    type CrucibleMember,
    type EssencePool,
    type RecipeDefinition,
    type StartingCrystalPick,
} from '@sorrem/shared';
import type { UIInventoryItem, UIPartyCharacter } from '@sorrem-client/types';
import { resolveHotkeyEquipSlotIndex } from '@sorrem-client/ui/shared/action-bar';
import { toEquipmentSlots, toItemSlotRef } from '@sorrem-client/ui/shared/equipment-slots';
import { useCurrentPlayerCharacter } from '@sorrem-client/ui/hooks';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useGetCharactersQuery, useMoveCharacterItemMutation } from '@sorrem-client/ui/store';
import { useCallback, useMemo, useState } from 'react';
import { isStartingCrystalId, toUIStartingCrystal } from './starting-crystal-staging';

const NO_PICKS: readonly StartingCrystalPick[] = [];

/** Where a carried item can be moved on the gathering page. */
export type GatheringMoveTarget = { readonly container: 'inventory' | 'equipment'; readonly index: number };

export interface StartingCrystalStaging {
    /** The character entering the run, or null while it loads. */
    character: UIPartyCharacter | null;
    /** Carried slots as the run will start them: the character's gear plus the crystals they've coalesced. */
    carriedItems: Array<UIInventoryItem | undefined>;
    totalSlots: number;
    /** Banked essence the coalesced crystals haven't claimed yet. */
    availableEssence: EssencePool;
    /** Coalesces one crystal into a carried slot; resolves with null, or why it couldn't. */
    coalesce: (slotIndex: number, recipe: RecipeDefinition) => Promise<string | null>;
    /** Un-makes one crystal from the stack in a carried slot. */
    removeCrystal: (slotIndex: number) => Promise<string | null>;
    /** Moves a carried item (gear or a crystal stack) or equipped gear to another slot, swapping with what is there. */
    moveItem: (itemId: string, target: GatheringMoveTarget) => Promise<string | null>;
    /** Equips a carried piece of gear, swapping out what it replaces. */
    equip: (item: UIInventoryItem) => Promise<string | null>;
}

interface UseStartingCrystalStagingOptions {
    readonly members: readonly CrucibleMember[];
    readonly stageStartingCrystals: (picks: StartingCrystalPick[]) => Promise<string | null>;
}

/**
 * The gathering page's carried inventory. Crystals coalesced here are picks
 * the server holds (it checks them against the character's banked essence and
 * charges them when the run starts), shown as stacks in their carried slots
 * through the same `resolveStartingCrystalPicks` the server uses. Gear moves
 * are saved to the character as on Character Home.
 */
export function useStartingCrystalStaging({ members, stageStartingCrystals }: UseStartingCrystalStagingOptions): StartingCrystalStaging {
    const { character } = useCurrentPlayerCharacter();
    // Same cache entry as useCurrentPlayerCharacter, which keys it by the lower-cased account id.
    const accountId = getStoredCanonicalAccountId();
    const { data: characters } = useGetCharactersQuery(accountId ?? '', { skip: accountId === null });
    const [moveCharacterItem] = useMoveCharacterItemMutation();
    const accountCharacter = useMemo<AccountCharacter | undefined>(
        () => characters?.find((entry) => entry.id === character?.id),
        [character?.id, characters],
    );

    const savedPicks = members.find((member) => member.characterId === character?.id)?.startingCrystalPicks ?? NO_PICKS;
    // Shown while the server checks a change; cleared once it answers (its party state follows).
    const [pendingPicks, setPendingPicks] = useState<readonly StartingCrystalPick[] | null>(null);
    const picks = pendingPicks ?? savedPicks;

    const preview = useMemo(() => {
        if (character === null || accountCharacter === undefined) {
            return null;
        }
        const totalSlots = accountCharacter.maxCarriedSlots;
        const resolved = resolveStartingCrystalPicks(
            picks,
            accountCharacter.carriedItems.map((item) => item ?? undefined),
            totalSlots,
            null,
            () => 'starting-crystal',
        );
        const carriedItems = Array.from({ length: totalSlots }, (_, slotIndex) => {
            const item = resolved.inventory[slotIndex];
            return isEmpyrealCrystalItem(item) ? toUIStartingCrystal(item, slotIndex) : character.carriedItems[slotIndex];
        });
        const availableEssence = withEssenceSubtracted(
            { ...createEmptyEssencePool(), ...accountCharacter.permanentEssence },
            resolved.cost,
        );
        return { carriedItems, totalSlots, availableEssence };
    }, [accountCharacter, character, picks]);

    const stage = useCallback(async (next: StartingCrystalPick[]): Promise<string | null> => {
        setPendingPicks(next);
        const error = await stageStartingCrystals(next);
        setPendingPicks(null);
        return error;
    }, [stageStartingCrystals]);

    const saveMove = useCallback(async (itemId: string, target: GatheringMoveTarget): Promise<string | null> => {
        if (accountId === null || character === null) {
            return 'Your character is still loading.';
        }
        try {
            await moveCharacterItem({
                accountId,
                characterId: character.id,
                itemId,
                target: toItemSlotRef(target.container, target.index),
            }).unwrap();
            return null;
        } catch {
            return 'That item could not be moved.';
        }
    }, [accountId, character, moveCharacterItem]);

    const carriedItems = preview?.carriedItems;
    const moveItem = useCallback(async (itemId: string, target: GatheringMoveTarget): Promise<string | null> => {
        if (carriedItems === undefined || character === null) {
            return null;
        }
        const fromSlot = carriedItems.findIndex((item) => item?.id === itemId);
        const targetItem = target.container === 'inventory' ? carriedItems[target.index] : undefined;
        const isTargetCrystal = targetItem !== undefined && isStartingCrystalId(targetItem.id);

        if (isStartingCrystalId(itemId)) {
            // Crystals only move between carried slots; gear in the way trades places with them.
            if (target.container !== 'inventory' || fromSlot < 0) {
                return null;
            }
            if (targetItem !== undefined && !isTargetCrystal) {
                const error = await saveMove(targetItem.id, { container: 'inventory', index: fromSlot });
                if (error !== null) {
                    return error;
                }
            }
            return await stage(withStartingCrystalPicksMoved(picks, fromSlot, target.index));
        }

        if (isTargetCrystal) {
            // Gear onto crystals: the crystals take the gear's carried slot, and gear from the loadout has none to give.
            if (fromSlot < 0) {
                return 'Move those crystals to an empty slot first.';
            }
            const error = await saveMove(itemId, target);
            return error ?? await stage(withStartingCrystalPicksMoved(picks, target.index, fromSlot));
        }
        return await saveMove(itemId, target);
    }, [carriedItems, character, picks, saveMove, stage]);

    const equip = useCallback(async (item: UIInventoryItem): Promise<string | null> => {
        if (character === null) {
            return null;
        }
        const slotIndex = resolveHotkeyEquipSlotIndex(item.equipmentType, toEquipmentSlots(getStoredLoadout(character)), isBothHanded(item));
        return slotIndex < 0 ? null : await moveItem(item.id, { container: 'equipment', index: slotIndex });
    }, [character, moveItem]);

    const coalesce = useCallback(
        (slotIndex: number, recipe: RecipeDefinition): Promise<string | null> => stage([...picks, { recipeId: recipe.id, slotIndex }]),
        [picks, stage],
    );
    const removeCrystal = useCallback(
        (slotIndex: number): Promise<string | null> => stage(withoutLastStartingCrystalPick(picks, slotIndex)),
        [picks, stage],
    );

    return {
        character,
        carriedItems: preview?.carriedItems ?? [],
        totalSlots: preview?.totalSlots ?? 0,
        availableEssence: preview?.availableEssence ?? createEmptyEssencePool(),
        coalesce,
        removeCrystal,
        moveItem,
        equip,
    };
}
